import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { Formik, FormikProps } from 'formik';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { RootState } from 'slices';
import {
  setCustomerModalOpen,
  setActiveCustomerId,
  setShowAllValidateCustomerErrors,
  resetCustomerForm,
} from 'slices/customer/reducer';

import { postAddCustomer, putUpdateCustomer } from 'services/customerService';
import {
  mapToCreateCustomerRequest,
  mapToUpdateCustomerRequest,
} from 'utils/mappers/customerMapper';
import { CustomerFormValidationSchema } from './validationSchema';

// import ManageCustomerForm from './ManageCustomerForm';

import { CustomerFormType, PickupAddress } from 'types/customer/customerTypes';
import { RESPONSE_MESSAGE } from 'pages/Customers/CustomerConstants';
import 'styles/common/title-text.css';
import ManageCustomerFormWithTabsV2 from './ManageCustomerFormWithTabsV2';
import { getCustomerErrorTab } from 'utils/common/yupError';
import AlertNotification from 'Components/pos2u/Alerts/AlertNotification';

interface CustomerModalProps {
  fetchCustomers: () => void;
  closeAddAndEditCustomers: boolean;
  availableBranchAccess: boolean;
}

const CustomerModal = ({
  fetchCustomers,
  closeAddAndEditCustomers,
  availableBranchAccess,
}: CustomerModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formikRef = useRef<FormikProps<CustomerFormType>>(null);

  const {
    customerModalOpen,
    isEdit,
    activeCustomerId,
    CustomerValues,
    countSubmit,
  } = useSelector((state: RootState) => state.Customer);

  const [reqDeletePickupAddr, setReqDeletePickupAddr] = useState<
    PickupAddress[]
  >([]);

  const handleDeletePickupAddr = useCallback((option: PickupAddress) => {
    setReqDeletePickupAddr((prev) => [...prev, option]);
  }, []);

  const toggleModal = useCallback(
    (id: string | null = null) => {
      dispatch(setCustomerModalOpen(!customerModalOpen));
      dispatch(setActiveCustomerId(id));
    },
    [customerModalOpen, dispatch]
  );

  const closeModal = () => {
    toggleModal(null);
    setShowError(false);
    setErrorMessage('');
  };

  const submitForm = async (values: CustomerFormType) => {
    try {
      let response;
      if (isEdit && activeCustomerId) {
        response = await putUpdateCustomer(
          activeCustomerId,
          mapToUpdateCustomerRequest(values, reqDeletePickupAddr)
        );
      } else {
        response = await postAddCustomer(mapToCreateCustomerRequest(values));
      }

      if (response && response.message === 'OK' && response.error_code === 0) {
        toast.success(
          isEdit
            ? t(RESPONSE_MESSAGE.UPDATE_SUCCESS)
            : t(RESPONSE_MESSAGE.CREATE_SUCCESS),
          {
            autoClose: 3000,
          }
        );
        closeModal();
        dispatch(setShowAllValidateCustomerErrors(false));
        fetchCustomers();
      } else {
        toast.error(t(response.message) || 'Error occurred', {
          autoClose: 3000,
        });
      }
    } catch (error: any) {
      toast.error(t(error.message) || 'Unexpected error', { autoClose: 3000 });
    }
  };

  // useEffect(() => {
  //   if (formikRef.current?.errors) {
  //     const errorKeys = Object.keys(formikRef.current.errors);
  //     const firstErrorField = document.querySelector(
  //       `[name="${errorKeys[0]}"]`
  //     ) as HTMLInputElement | null;
  //     if (firstErrorField) firstErrorField.focus();
  //   }
  // }, [formikRef, countSubmit]);

  const generalTabRef = useRef<any>(null);
  const pickupTabRef = useRef<any>(null);
  const alertRef = useRef<HTMLDivElement | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    const tab = getCustomerErrorTab(formikRef.current?.errors);

    if (!tab) {
      setShowError(false);
      setErrorMessage('');
      return;
    }

    if (tab === 'pickup') {
      setErrorMessage(
        t('PleaseFillInAllRequiredFields', { tabName: t('Pickup Address') })
      );
    } else if (tab === 'general') {
      setErrorMessage(
        t('PleaseFillInAllRequiredFields', { tabName: t('General') })
      );
    }

    setShowError(true);
  }, [countSubmit, t]);

  // useEffect(() => {
  //   if (alertRef.current) {
  //     alertRef.current?.focus();
  //   }
  // }, [countSubmit]);

  return (
    <div className="page-content">
      <Container fluid>
        <Modal
          size="xl"
          isOpen={customerModalOpen}
          toggle={() => {
            closeModal();
            dispatch(setShowAllValidateCustomerErrors(false));
          }}
          backdrop="static"
          centered
        >
          <ModalHeader
            className="bg-light p-3"
            toggle={() => {
              closeModal();
              dispatch(setShowAllValidateCustomerErrors(false));
              dispatch(resetCustomerForm());
            }}
          >
            {isEdit ? t('Edit Customer') : t('Create Customer')}
            {/* <div className="px-3">
              {isEdit ? t('Edit Customer') : t('Create Customer')}
            </div> */}
            {/* <div style={{ paddingLeft: '10px' }}>{isEdit ? t('Edit Customer') : t('Create Customer')}</div> */}
          </ModalHeader>
          <ModalBody>
            <Formik<CustomerFormType>
              initialValues={CustomerValues}
              validationSchema={CustomerFormValidationSchema(t)}
              onSubmit={submitForm}
              enableReinitialize
              innerRef={formikRef}
            >
              {(formik) => (
                <form noValidate onSubmit={formik.handleSubmit}>
                  {/* <ManageCustomerForm
                    formik={formik}
                    closeAddAndEditCustomers={closeAddAndEditCustomers}
                    availableBranchAccess={availableBranchAccess}
                  /> */}
                  {showError && errorMessage && (
                    <div
                      ref={alertRef}
                      tabIndex={-1}
                      aria-live="assertive"
                      className="mb-3"
                    >
                      <AlertNotification
                        type="error"
                        // message={errorMessage}
                        heading={errorMessage}
                        closeAction={() => {
                          setShowError(false);
                        }}
                      />
                    </div>
                  )}

                  <ManageCustomerFormWithTabsV2
                    formik={formik}
                    closeAddAndEditCustomers={closeAddAndEditCustomers}
                    availableBranchAccess={availableBranchAccess}
                    handleDeletePickupAddr={handleDeletePickupAddr}
                    generalTabRef={generalTabRef}
                    pickupTabRef={pickupTabRef}
                  />
                </form>
              )}
            </Formik>
          </ModalBody>
        </Modal>
        <ToastContainer closeButton={false} limit={1} />
      </Container>
    </div>
  );
};

export default CustomerModal;
